import { toast } from 'react-toastify'

/**
 * Checks if a string is empty, containing only empty lines or lines with whitespace characters.
 * @param {string} value - The string to check.
 * @returns {boolean} True if the string is empty, false otherwise.
 */
export const isEmptyField = (value) => {
	let a = String(value).match(/((\r\n|\n|\r)$)|(^(\r\n|\n|\r))|^\s*$/gm)
	return a !== null ? true : false
}

/**
 * Validates an email address using a regular expression.
 * @param {string} email - The email address to validate.
 * @returns {boolean} True if the email address is valid, false otherwise.
 */
export const validateEmail = (email) => {
	let a = String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		)
	return a !== null ? true : false
}

/**
 * Validates a password using a regular expression.
 * @param {string} password - The password to validate.
 * @returns {boolean} True if the password is valid, false otherwise.
 */
export const validatePassword = (password) => {
	let a = String(password).match(
		/(?=[A-Za-z0-9@#$%^&+!=]+$)^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+!=])(?=.{8,}).*$/g,
	)
	return a !== null ? true : false
}

/**
 * Validates a phone number using a regular expression.
 * @param {string} phone - The phone number to be validated.
 * @returns {boolean} - Returns true if the phone number is valid, false otherwise.
 */
export const validatePhone = (phone) => {
	// Remove all non-digit characters from the phone number
	const cleanedPhone = phone.replace(/\D/g, '')

	// Define regular expressions for different phone number formats
	const patterns = [
		// International format (+[country code] [area code] [local number])
		/^\+?\d{1,3}?[-\s]?\(?\d{2,3}\)?[-\s]?\d{3}[-\s]?\d{4}$/,
		// Indian format (0[area code][local number])
		/^0\d{10}$/,
		// US format ([area code][local number])
		/^\d{3}[-\s]?\d{3}[-\s]?\d{4}$/,
	]

	// Check if the phone number matches any of the patterns
	return patterns.some((pattern) => pattern.test(cleanedPhone))
}

export function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

export function getTimeAgoFromDate(publishDateStr) {
	try {
		const publishDate = new Date(publishDateStr)
		const currentDate = new Date()

		// Add 5 hours and 30 minutes to match IST timezone
		const adjustedPublishDate = new Date(publishDate.getTime() + 5.5 * 60 * 60 * 1000)

		// Calculate difference in milliseconds
		const diffTime = currentDate.getTime() - adjustedPublishDate.getTime()

		// Convert to minutes and hours
		const diffMinutes = Math.abs(Math.round(diffTime / (1000 * 60)))
		const diffHours = Math.abs(Math.floor(diffTime / (1000 * 60 * 60)))
		const diffDays = Math.abs(Math.floor(diffTime / (1000 * 60 * 60 * 24)))

		// Handle different time ranges
		if (diffMinutes < 5) {
			return 'Just now'
		} else if (diffMinutes < 60) {
			return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`
		} else if (diffHours < 24) {
			return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
		} else {
			return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`
		}
	} catch (error) {
		console.error('Error calculating time ago:', error)
		return 'Invalid date'
	}
}

export function calculateReadingTime(htmlContent) {
	try {
		// Strip HTML tags and decode HTML entities
		const strippedContent = htmlContent
			.replace(/<[^>]*>/g, '') // Remove HTML tags
			.replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
			.replace(/\s+/g, ' ') // Replace multiple spaces with single space
			.trim()

		// Average reading speed (words per minute)
		const wordsPerMinute = 200

		// Count words (split by spaces and filter out empty strings)
		const wordCount = strippedContent.split(/\s+/).filter((word) => word.length > 0).length

		// Calculate reading time in minutes
		const readingTime = Math.ceil(wordCount / wordsPerMinute)

		// Format the output
		if (readingTime <= 1) {
			return '1 min'
		} else {
			return `${readingTime} mins`
		}
	} catch (error) {
		console.error('Error calculating reading time:', error)
		return '1 min' // fallback
	}
}

export function formatDate(dateString) {
	try {
		const date = new Date(dateString)

		// Using Intl.DateTimeFormat for formatting
		const formatter = new Intl.DateTimeFormat('en-US', {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		})

		return formatter.format(date)
	} catch (error) {
		console.error('Error formatting date:', error)
		return 'Invalid date'
	}
}

export function formatDateCustom(dateString, options = {}) {
	try {
		const date = new Date(dateString)

		const formatOptions = {
			day: options.dayFormat || 'numeric',
			month: options.monthFormat || 'short',
			year: options.yearFormat || 'numeric',
			weekday: options.weekday,
		}

		const formatter = new Intl.DateTimeFormat(options.locale || 'en-US', formatOptions)
		return formatter.format(date)
	} catch (error) {
		console.error('Error formatting date:', error)
		return 'Invalid date'
	}
}

export const shareContent = async (data) => {
	// Destructure the share data with defaults
	let { title = document.title, text = '', url } = data

	if (url && !url.includes(window.location.hostname)) {
		url = `${window.location.origin}${url}`
	}

	try {
		// Check if the Web Share API is available
		if (navigator.share) {
			await navigator.share({
				title,
				text,
				url,
			})
			return
		}

		if (!url) return
		// Fallback to clipboard if Web Share API is not available
		// Check if the clipboard API is available
		if (navigator.clipboard && window.isSecureContext) {
			await navigator.clipboard.writeText(url)
			toast.success('Link copied to clipboard!', {
				autoClose: 5000,
				hideProgressBar: false,
			})
			return
		}

		// Fallback for older browsers
		const textarea = document.createElement('textarea')
		textarea.value = url
		textarea.style.position = 'fixed'
		textarea.style.left = '-999999px'
		textarea.style.top = '-999999px'
		document.body.appendChild(textarea)
		textarea.focus()
		textarea.select()

		try {
			document.execCommand('copy')
			toast.success('Link copied to clipboard!', {
				autoClose: 5000,
				hideProgressBar: false,
			})
		} catch (err) {
			console.error('Failed to copy text: ', err)
		} finally {
			textarea.remove()
		}
	} catch (error) {
		console.error('Error sharing content: ', error)
		throw error
	}
}
